/**
 * Financial Parameters Section - Business Value Calculator
 * Sprint 7: Day 2
 *
 * Core financial assumptions for NPV/IRR/ROI calculations:
 * - Discount Rate (WACC): 8-12% typical for pharma/biotech
 * - Tax Rate: 21-35% depending on jurisdiction
 * - Inflation Rate: 2-4% annual
 *
 * These parameters are critical for accurate financial modeling.
 * Default values reflect industry standards for life sciences organizations.
 */

import React from 'react';

export interface FinancialParameters {
  discountRate: number;     // Annual discount rate (WACC) in percentage
  taxRate: number;          // Corporate tax rate in percentage
  inflationRate: number;    // Annual inflation rate in percentage
}

interface FinancialParametersSectionProps {
  parameters: FinancialParameters;
  onParameterUpdate: (field: keyof FinancialParameters, value: number) => void;
}

export const FinancialParametersSection: React.FC<FinancialParametersSectionProps> = ({
  parameters,
  onParameterUpdate
}) => {
  // Handle input change with validation
  const handleInputChange = (field: keyof FinancialParameters, value: string) => {
    const numericValue = parseFloat(value) || 0;

    // Validate ranges
    if (field === 'discountRate' && (numericValue < 0 || numericValue > 30)) return;
    if (field === 'taxRate' && (numericValue < 0 || numericValue > 50)) return;
    if (field === 'inflationRate' && (numericValue < 0 || numericValue > 20)) return;

    onParameterUpdate(field, numericValue);
  };

  return (
    <div className="financial-parameters-section">
      <div className="section-help-text">
        <strong>Financial Parameters:</strong> Core assumptions for NPV, IRR, and ROI calculations.
        These values significantly impact your financial results.
        Default values reflect industry standards for life sciences organizations.
      </div>

      <div className="parameters-grid">
        {/* Discount Rate */}
        <div className="parameter-card">
          <div className="parameter-icon" style={{ background: 'linear-gradient(135deg, #0094ab 0%, #004772 100%)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="parameter-content">
            <label htmlFor="discount-rate" className="parameter-label">
              Discount Rate (WACC)
            </label>
            <div className="parameter-description">
              Annual weighted average cost of capital. Used to calculate present value of future cash flows.
              Typical range: 8-12% for pharma/biotech.
            </div>
            <div className="parameter-input-group">
              <input
                id="discount-rate"
                type="number"
                min="0"
                max="30"
                step="0.5"
                value={parameters.discountRate}
                onChange={(e) => handleInputChange('discountRate', e.target.value)}
                className="parameter-input"
                aria-label="Discount rate percentage"
              />
              <span className="parameter-unit">%</span>
            </div>
            <input
              type="range"
              min="0"
              max="30"
              step="0.5"
              value={parameters.discountRate}
              onChange={(e) => handleInputChange('discountRate', e.target.value)}
              className="parameter-slider"
              aria-label="Discount rate slider"
            />
            <div className="parameter-range-labels">
              <span>0%</span>
              <span>30%</span>
            </div>
          </div>
        </div>

        {/* Tax Rate */}
        <div className="parameter-card">
          <div className="parameter-icon" style={{ background: 'linear-gradient(135deg, #48bb78 0%, #38a169 100%)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="3" y1="9" x2="21" y2="9" />
              <line x1="9" y1="21" x2="9" y2="9" />
            </svg>
          </div>
          <div className="parameter-content">
            <label htmlFor="tax-rate" className="parameter-label">
              Corporate Tax Rate
            </label>
            <div className="parameter-description">
              Effective corporate income tax rate for your organization.
              Typical range: 21% (US federal) to 35% (state/local included).
            </div>
            <div className="parameter-input-group">
              <input
                id="tax-rate"
                type="number"
                min="0"
                max="50"
                step="1"
                value={parameters.taxRate}
                onChange={(e) => handleInputChange('taxRate', e.target.value)}
                className="parameter-input"
                aria-label="Tax rate percentage"
              />
              <span className="parameter-unit">%</span>
            </div>
            <input
              type="range"
              min="0"
              max="50"
              step="1"
              value={parameters.taxRate}
              onChange={(e) => handleInputChange('taxRate', e.target.value)}
              className="parameter-slider"
              aria-label="Tax rate slider"
            />
            <div className="parameter-range-labels">
              <span>0%</span>
              <span>50%</span>
            </div>
          </div>
        </div>

        {/* Inflation Rate */}
        <div className="parameter-card">
          <div className="parameter-icon" style={{ background: 'linear-gradient(135deg, #ed8936 0%, #dd6b20 100%)' }}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="12" y1="5" x2="12" y2="19" />
              <polyline points="18 12 12 5 6 12" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </div>
          <div className="parameter-content">
            <label htmlFor="inflation-rate" className="parameter-label">
              Annual Inflation Rate
            </label>
            <div className="parameter-description">
              Expected annual inflation rate for cost escalation over 36 months.
              Typical range: 2-4% (US Federal Reserve target: 2%).
            </div>
            <div className="parameter-input-group">
              <input
                id="inflation-rate"
                type="number"
                min="0"
                max="20"
                step="0.1"
                value={parameters.inflationRate}
                onChange={(e) => handleInputChange('inflationRate', e.target.value)}
                className="parameter-input"
                aria-label="Inflation rate percentage"
              />
              <span className="parameter-unit">%</span>
            </div>
            <input
              type="range"
              min="0"
              max="20"
              step="0.1"
              value={parameters.inflationRate}
              onChange={(e) => handleInputChange('inflationRate', e.target.value)}
              className="parameter-slider"
              aria-label="Inflation rate slider"
            />
            <div className="parameter-range-labels">
              <span>0%</span>
              <span>20%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Impact Summary */}
      <div className="parameters-impact">
        <div className="parameters-impact-header">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="16" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12.01" y2="8" />
          </svg>
          <strong>How These Parameters Affect Your Results:</strong>
        </div>
        <ul className="parameters-impact-list">
          <li>
            <strong>Higher Discount Rate:</strong> Reduces NPV (future benefits worth less today). Conservative financial view.
          </li>
          <li>
            <strong>Higher Tax Rate:</strong> Reduces after-tax cash flows and ROI. Important for tax shield calculations.
          </li>
          <li>
            <strong>Higher Inflation:</strong> Increases future costs but may also increase future benefits. Net effect varies by scenario.
          </li>
        </ul>
      </div>

      <style>{`
        .section-help-text {
          background: rgba(0, 148, 171, 0.05);
          border-left: 4px solid #0094ab;
          border-radius: 8px;
          padding: 1rem;
          margin-bottom: 1.5rem;
          font-size: 0.875rem;
          color: #4a5568;
          line-height: 1.6;
        }

        .parameters-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 1.5rem;
          margin-top: 1.5rem;
        }

        .parameter-card {
          display: flex;
          gap: 1rem;
          padding: 1.5rem;
          background: #ffffff;
          border: 2px solid #e2e8f0;
          border-radius: 12px;
          transition: all 0.2s ease;
        }

        .parameter-card:hover {
          border-color: #cbd5e0;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
        }

        .parameter-icon {
          flex-shrink: 0;
          width: 48px;
          height: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          color: #ffffff;
        }

        .parameter-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .parameter-label {
          font-size: 0.875rem;
          font-weight: 700;
          color: #2d3748;
          text-transform: uppercase;
          letter-spacing: 0.05em;
        }

        .parameter-description {
          font-size: 0.75rem;
          color: #718096;
          line-height: 1.5;
        }

        .parameter-input-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .parameter-input {
          width: 80px;
          padding: 0.5rem 0.75rem;
          font-size: 1rem;
          font-weight: 600;
          text-align: right;
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          transition: all 0.2s ease;
        }

        .parameter-input:focus {
          outline: none;
          border-color: #0094ab;
          box-shadow: 0 0 0 3px rgba(0, 148, 171, 0.1);
        }

        .parameter-unit {
          font-size: 1rem;
          font-weight: 600;
          color: #718096;
        }

        .parameter-slider {
          width: 100%;
          height: 6px;
          border-radius: 3px;
          background: linear-gradient(to right, #e2e8f0 0%, #0094ab 100%);
          outline: none;
          -webkit-appearance: none;
          appearance: none;
          cursor: pointer;
        }

        .parameter-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #0094ab;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }

        .parameter-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #0094ab;
          border: 2px solid #ffffff;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
          cursor: pointer;
        }

        .parameter-slider:focus::-webkit-slider-thumb {
          box-shadow: 0 0 0 3px rgba(0, 148, 171, 0.3);
        }

        .parameter-slider:focus::-moz-range-thumb {
          box-shadow: 0 0 0 3px rgba(0, 148, 171, 0.3);
        }

        .parameter-range-labels {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          color: #a0aec0;
          margin-top: -0.25rem;
        }

        .parameters-impact {
          margin-top: 2rem;
          padding: 1.5rem;
          background: linear-gradient(135deg, rgba(0, 148, 171, 0.05) 0%, rgba(0, 71, 114, 0.05) 100%);
          border: 1px solid rgba(0, 148, 171, 0.2);
          border-radius: 12px;
        }

        .parameters-impact-header {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.875rem;
          color: #0094ab;
          margin-bottom: 1rem;
        }

        .parameters-impact-list {
          list-style: none;
          padding: 0;
          margin: 0;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .parameters-impact-list li {
          font-size: 0.875rem;
          color: #4a5568;
          line-height: 1.6;
          padding-left: 1.5rem;
          position: relative;
        }

        .parameters-impact-list li::before {
          content: '→';
          position: absolute;
          left: 0;
          color: #0094ab;
          font-weight: 700;
        }

        /* Responsive Design */
        @media (max-width: 968px) {
          .parameters-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>
    </div>
  );
};

// Default financial parameters (industry standard for life sciences)
export const DEFAULT_FINANCIAL_PARAMETERS: FinancialParameters = {
  discountRate: 10,      // 10% WACC (typical for pharma/biotech)
  taxRate: 25,           // 25% effective tax rate
  inflationRate: 3       // 3% annual inflation
};

export default FinancialParametersSection;
